import { useCallback, useContext, useEffect, useState } from "react";
import AuthenticatedContractDetails from "../../../pages/contract/authenticatedContractDetails";
import { PublicContractDetails } from "../../../pages/contract/publicContractDetails";
import { isLoggedIn } from "../authContext";
import { ContractContext } from "../../../providers/contract";
import {
  EContractType,
  IContractDetailsResponseData,
} from "../../api/services/contract/models";
import MilestoneContractDetails from "../../../pages/contract/milestoneContractDetails";
import ComplexContractDetails from "../../../pages/business/complexContractDetails";
import { useNavigate, useParams } from "react-router-dom";
import {
  deleteExternalContractById,
  getExternalMilestoneContracts,
} from "../../api/services/contract";
import { ROUTES } from "../../constants";
import MilestoneDetails from "../../../pages/business/milestoneDetails";
import { AuthConfig } from "../config/authConfig";

type ExtendedContract = IContractDetailsResponseData & {
  title: string;
  deadline: string;
};

const ContractRoute = ({ isAdmin }: { isAdmin: boolean }) => {
  const { contractType, contract } = useContext(ContractContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const [milestones, setMilestones] = useState<ExtendedContract[]>([]);

  const fetchMilestoneDetails = useCallback(
    async (id: string) => {
      try {
        const response = await getExternalMilestoneContracts({
          complexParentId: id,
          pageSize: contract?.activeChildrenCount,
        });
        setMilestones(
          response?.data?.content?.map((content) => ({
            ...content,
            title: content?.itemDetail?.title,
            deadline: content?.deadLine || "",
          }))
        );
      } catch (error) {
        navigate(ROUTES.CONTRACT_NOT_FOUND);
        console.error(error);
      }
    },
    [contract?.activeChildrenCount, navigate]
  );

  useEffect(() => {
    if (id && contractType === EContractType.COMPLEX_CONTRACT && !isAdmin)
      fetchMilestoneDetails(id);
  }, [contractType, fetchMilestoneDetails, id, isAdmin]);

  if (!contract) {
    return <div></div>;
  }

  if (contractType === EContractType.COMPLEX_CONTRACT) {
    return isAdmin ? (
      <MilestoneContractDetails />
    ) : authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
      authorities?.includes("CLIENT_ADMIN") ||
      authorities?.includes("ORGANIZATION_OWNER") ? (
      <ComplexContractDetails
        title={contract?.itemDetail?.title || ""}
        description={contract?.itemDetail?.description || ""}
        milestonesArray={milestones}
        totalAmount={contract?.activeChildrenTotalAmount}
        totalMilestones={contract?.activeChildrenCount}
        totalParties={contract?.activeChildrenPartiesCount}
        showBreadCrumbs
        handleAddMilestone={() =>
          navigate(
            ROUTES.CREATE_COMPLEX_CONTRACT.replace(
              ":complexId?/",
              id || ""
            ).replace(":milestoneId?", "")
          )
        }
        handleDeleteMilestone={async (index) => {
          await deleteExternalContractById(milestones[index]?.id || "");
          window.location.reload();
        }}
      />
    ) : (
      <></>
    );
  }

  if (
    contractType === EContractType.MILESTONE_CONTRACT &&
    !isAdmin &&
    (authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
      authorities?.includes("CLIENT_ADMIN") ||
      authorities?.includes("ORGANIZATION_OWNER"))
  ) {
    return <MilestoneDetails />;
  }

  return isLoggedIn() ? (
    <AuthenticatedContractDetails />
  ) : (
    <PublicContractDetails />
  );
};

export default ContractRoute;
